import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouteError } from "./components/nav/route-error";
import { pruneDrafts } from "./lib/use-draft";
import { routeTree } from "./routeTree.gen";
import "./styles/globals.css";

/**
 * Client entry. Wires TanStack Router (file-based routes generated
 * by the Vite plugin) and TanStack Query (server-state cache, the
 * equivalent of RSC fetches in the old Next stack).
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // RSC equivalent had no client-side stale time; with a real
      // cache we can be aggressive on dedup but conservative on
      // staleness. 30 s feels right for hub data — inboxes,
      // dashboards, work items don't churn faster than that.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // A failing page errors in place; the layout around it stays.
  defaultErrorComponent: RouteError,
});

pruneDrafts();

// TanStack Router augmentation so `Link to="..."` etc. is fully
// type-safe against the generated route tree.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error('Missing <div id="root"> in index.html');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

// Service worker: the app then opens without a signal too, so a receipt
// can be photographed in a car park and sent once there is network. It
// registers after load so it doesn't compete with the first render.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // Without the service worker the app still works, just not offline.
    });
  });
}
